import React, { useState } from 'react';
import { Card, Input, Row, Col, Space, Tag } from 'antd';
import { AccountBookOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { DataTable, MoneyCell, PageLayout } from '../../ui';
import dayjs from 'dayjs';
import { useGetPayablesListQuery, PayableListItem } from '../../store/api/financeApi';

const STATUS_COLORS: Record<string, string> = {
  PAID: 'success',
  PARTIAL: 'processing',
  OVERDUE: 'error',
  PENDING: 'default'
};

const STATUS_LABELS: Record<string, string> = {
  PAID: 'Оплачено',
  PARTIAL: 'Частично',
  OVERDUE: 'Просрочено',
  PENDING: 'Ожидает'
};

function getPayableStatus(row: PayableListItem): string {
  const { remaining, amount, dueDate } = row;
  if (remaining === 0) return 'PAID';
  if (!dueDate) return remaining > 0 && remaining < amount ? 'PARTIAL' : 'PENDING';
  const today = dayjs().startOf('day');
  const due = dayjs(dueDate).startOf('day');
  if (!due.isValid()) return remaining > 0 && remaining < amount ? 'PARTIAL' : 'PENDING';
  if (remaining > 0 && due.isBefore(today)) return 'OVERDUE';
  if (remaining > 0 && remaining < amount) return 'PARTIAL';
  return 'PENDING';
}

const FinancePayablesPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState<string>('');

  const { data, isLoading } = useGetPayablesListQuery({
    page,
    limit,
    search: search.trim() || undefined
  });

  const payables = data?.payables ?? [];
  const pagination = data?.pagination ?? { page: 1, limit: 20, total: 0 };

  const columns: ColumnsType<PayableListItem> = [
    {
      title: 'Перевозчик',
      key: 'carrier',
      ellipsis: true,
      render: (_, r) => r.route?.carrier?.name ?? '—'
    },
    {
      title: 'Маршрут',
      key: 'route',
      width: 90,
      render: (_, r) => (r.route ? `#${r.route.id}` : '—')
    },
    {
      title: 'Сумма',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right',
      render: (v: number) => <MoneyCell value={Number(v ?? 0)} type="expense" />
    },
    {
      title: 'Оплачено',
      dataIndex: 'paid',
      key: 'paid',
      width: 120,
      align: 'right',
      render: (v: number) => <MoneyCell value={Number(v ?? 0)} type="expense" />
    },
    {
      title: 'Остаток',
      dataIndex: 'remaining',
      key: 'remaining',
      width: 120,
      align: 'right',
      render: (v: number) => <MoneyCell value={Number(v ?? 0)} type="expense" />
    },
    {
      title: 'Срок оплаты',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 120,
      render: (d: string) => (d ? dayjs(d).format('DD.MM.YYYY') : '—')
    },
    {
      title: 'Статус',
      key: 'statusDisplay',
      width: 120,
      render: (_, r) => {
        const status = getPayableStatus(r);
        return (
          <Tag color={STATUS_COLORS[status] || 'default'}>
            {STATUS_LABELS[status] || status}
          </Tag>
        );
      }
    }
  ];

  return (
    <PageLayout
      title={
        <Space>
          <AccountBookOutlined />
          <span>Кредиторская задолженность</span>
        </Space>
      }
      subtitle="Остатки к оплате перевозчикам по обязательствам"
    >
      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col>
            <Input
              placeholder="Поиск по перевозчику"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              style={{ width: 220 }}
            />
          </Col>
        </Row>

        <DataTable<PayableListItem>
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={payables}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: false,
            showTotal: (t) => `Всего: ${t}`,
            onChange: (p) => setPage(p)
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>
    </PageLayout>
  );
};

export default FinancePayablesPage;

